import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  ArrowLeft, DollarSign, TrendingUp, TrendingDown, Fuel, Wrench, MapPin, Truck, ArrowRight, Activity,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMoneyShort = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return fmtMoney(n);
};

const TruckPnL = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/analytics/truck-pnl?days=${days}`, { withCredentials: true });
        if (mounted) setData(res.data);
      } catch (e) {
        if (mounted) setError(e.response?.data?.detail || 'Failed to load P&L');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [days]);

  const chartData = useMemo(() => {
    if (!data?.trucks) return [];
    return data.trucks.map(t => ({
      name: t.label,
      revenue: t.revenue,
      fuel: t.fuel_cost,
      maintenance: t.maintenance_cost,
      profit: t.profit,
      asset_id: t.asset_id,
    }));
  }, [data]);

  if (loading) {
    return <DashboardLayout><div className="p-8 text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading P&amp;L...</div></DashboardLayout>;
  }
  if (error) {
    return <DashboardLayout><div className="p-8 text-red-600" data-testid="pnl-error">{error}</div></DashboardLayout>;
  }
  if (!data) return null;

  const { fleet, trucks } = data;
  const fleetMargin = fleet.margin_pct;
  const isPositive = fleet.profit >= 0;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="truck-pnl-page">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to dashboard
        </button>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Truck P&amp;L</h1>
            <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Revenue, fuel, maintenance, and profit-per-mile by truck.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Window</label>
            <select data-testid="window-select" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 6 months</option>
              <option value="365">Last 12 months</option>
            </select>
          </div>
        </div>

        {/* Fleet KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <KpiCard testid="fleet-revenue" icon={DollarSign} color="#002FA7" label="Revenue" value={fmtMoney(fleet.revenue)} onClick={() => navigate('/loads?status=delivered')} />
          <KpiCard testid="fleet-fuel" icon={Fuel} color="#FF9500" label="Fuel" value={fmtMoney(fleet.fuel_cost)} />
          <KpiCard testid="fleet-maintenance" icon={Wrench} color="#7C3AED" label="Maintenance" value={fmtMoney(fleet.maintenance_cost)} onClick={() => navigate('/assets')} />
          <KpiCard testid="fleet-profit" icon={isPositive ? TrendingUp : TrendingDown} color={isPositive ? '#34C759' : '#EF4444'} label="Profit" value={fmtMoney(fleet.profit)} />
          <KpiCard testid="fleet-margin" icon={Activity} color="#002FA7" label="Margin" value={fleetMargin != null ? `${fleetMargin}%` : '—'} />
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <KpiCard testid="fleet-miles" icon={MapPin} color="#FF9500" label="Total Miles" value={fleet.miles.toLocaleString()} onClick={() => navigate('/loads?status=delivered')} />
          <KpiCard testid="fleet-trucks" icon={Truck} color="#002FA7" label="Trucks" value={trucks.length} onClick={() => navigate('/assets')} />
          <KpiCard testid="fleet-ppm" icon={DollarSign} color={fleet.profit_per_mile && fleet.profit_per_mile >= 0 ? '#34C759' : '#EF4444'} label="Profit / Mile" value={fleet.profit_per_mile != null ? `$${fleet.profit_per_mile.toFixed(3)}` : '—'} />
        </div>

        {/* Chart */}
        {trucks.length > 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-6 mb-8" data-testid="pnl-chart">
            <h2 className="text-lg font-semibold tracking-tight text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>P&amp;L by Truck</h2>
            <p className="text-xs text-slate-500 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Click a bar to open that truck's detail page
            </p>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={0}>
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => fmtMoneyShort(v)} />
                  <Tooltip
                    contentStyle={{ border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }}
                    formatter={(v) => fmtMoney(v)}
                  />
                  <Bar
                    dataKey="profit"
                    name="Profit"
                    radius={[4, 4, 0, 0]}
                    cursor="pointer"
                    onClick={(entry) => entry?.asset_id && navigate(`/assets/${entry.asset_id}`)}
                  >
                    {chartData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.profit >= 0 ? '#34C759' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}

        {/* Per-truck table */}
        <div className="bg-white border border-slate-200 rounded-md" data-testid="pnl-table">
          <div className="px-5 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Per-Truck Breakdown</h2>
            <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Click a row to open truck details</p>
          </div>

          {trucks.length === 0 ? (
            <p className="p-8 text-center text-slate-500 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="no-trucks">No trucks yet. Add a truck under Assets to start tracking P&amp;L.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead className="bg-slate-50">
                  <tr>
                    <Th>Truck</Th>
                    <Th>Driver</Th>
                    <Th right>Loads</Th>
                    <Th right>Miles</Th>
                    <Th right>Revenue</Th>
                    <Th right>Fuel</Th>
                    <Th right>Maint.</Th>
                    <Th right>Profit</Th>
                    <Th right>$/mi</Th>
                    <Th right>Margin</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {trucks.map(t => {
                    const pos = (t.profit || 0) >= 0;
                    return (
                      <tr
                        key={t.asset_id}
                        data-testid={`pnl-row-${t.asset_id}`}
                        onClick={() => navigate(`/assets/${t.asset_id}`)}
                        className="hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{t.label}</p>
                          <p className="text-xs text-slate-500">{[t.make, t.model, t.year].filter(Boolean).join(' ')}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{t.driver_name || <span className="italic text-slate-400">Unassigned</span>}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{t.delivered_loads}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{t.miles.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-900">{fmtMoney(t.revenue)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{fmtMoney(t.fuel_cost)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{fmtMoney(t.maintenance_cost)}</td>
                        <td className={`px-4 py-3 text-right font-bold ${pos ? 'text-green-700' : 'text-red-700'}`}>{fmtMoney(t.profit)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${pos ? 'text-green-700' : 'text-red-700'}`}>
                          {t.profit_per_mile != null ? `$${t.profit_per_mile.toFixed(3)}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">{t.margin_pct != null ? `${t.margin_pct}%` : '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <ArrowRight className="w-4 h-4 text-slate-400 inline" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

const KpiCard = ({ testid, icon: Icon, label, value, color = '#002FA7', onClick }) => {
  const Component = onClick ? 'button' : 'div';
  return (
    <Component
      data-testid={testid}
      onClick={onClick}
      className={`bg-white border border-slate-200 rounded-md p-5 w-full text-left ${onClick ? 'cursor-pointer hover:border-[#002FA7] hover:shadow-sm transition-all' : ''}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-7 h-7" style={{ color }} strokeWidth={2} />
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
          <p className="text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
        </div>
      </div>
    </Component>
  );
};

const Th = ({ children, right }) => (
  <th className={`px-4 py-2 text-xs font-semibold uppercase tracking-widest text-slate-500 ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

export default TruckPnL;
